import { Button, ButtonWithLoader } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import type { AxiosError } from 'axios'
import { Play } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { isProd } from 'v2/configs/constants'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useBalanceData } from 'v2/shared/hooks/use-balance-data'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { useNatDetection } from 'v2/shared/hooks/use-nat-detection'
import { flowService } from 'v2/shared/services/flow-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { useTabStore } from 'v2/state/tab'
// Removed Popover imports - using custom implementation due to portal issues
import { FlowTypes } from 'v2/types'
import { PubnubStatus } from 'v2/types/pubnub'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { agentLogger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import { CALL_PLACING_NODE_TYPES } from '../../config/contact-screening.config'
import { useAgentSettingsFormValues } from '../../hooks/use-agent-settings-form-values'
import { usePublishFlow } from '../../hooks/use-publish-flow'
import { useStartNodeType } from '../../hooks/use-start-node-type'
import { useInitializePlaygroundMutation, useTriggerApiRequestMutation } from '../../queries'
import { RUN_CONFIRM_MAX_TRIES, RUN_CONFIRM_POLL_MS } from '../../run-info/hooks/use-run-events'
import { useFlowRunStore } from '../../run-info/store'
import { useFlowStore } from '../../store'
import type { JsonValue } from '../../types'
import { AgentState, type FlowData } from '../../types'
import { checkUnsavedChanges, isMigratedLegacyFlow } from '../../utils'
import { embedTestErrorToast, shouldGateOnBalance } from '../../utils/embed-test-agent'
import { isFlowCleanForPlayground } from '../../utils/playground-clean-check'
import { waitForMetaReady } from '../../utils/wait-for-meta-ready'
import { StartEvalButton, ViewResultsButton } from '../eval/run-eval-button'
import { useEvalStore } from '../eval/store'
import { ApiRequestPayloadForm } from './api-request-payload-form'
import { BalanceWarningPopoverContent } from './balance-warning-popover-content'
import { NatWarningPopoverContent } from './nat-warning-popover-content'
import { TestWithVibeButton } from './test-with-vibe-button'

enum PopoverType {
  API = 'api',
  BALANCE = 'balance',
  NAT = 'nat',
}

interface PlaygroundButtonProps {
  flowData?: FlowData
}

export const PlaygroundButton = ({ flowData }: PlaygroundButtonProps) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isSubmittingApiRequest, setIsSubmittingApiRequest] = useState(false)
  const [isWaitingForMeta, setIsWaitingForMeta] = useState(false)
  const { isManualEvalEnabled, isVibeEvalEnabled, vibeAgentDisabled } = useFeatureFlags()
  const {
    balance,
    currency,
    isPostPaid,
    hasValidData,
    hasError,
    isLoading: isBalanceLoading,
  } = useBalanceData()
  const { isBehindNat, isChecking: isNatChecking, recheck: recheckNat } = useNatDetection()
  const { data: profileData } = useProfileQuery()
  const isEmbed = isEmbedMode()
  const gateOnBalance = shouldGateOnBalance(isProd, isEmbed)
  const canTest = gateOnBalance ? hasValidData && (isPostPaid || balance >= 0.01) : true
  const effectiveBalanceLoading = gateOnBalance ? isBalanceLoading : false
  const [popoverContent, setPopoverContent] = useState<PopoverType>(PopoverType.API)

  const agentType = useStartNodeType()
  const setShowVibeSidebar = useFlowStore((state) => state.setShowVibeSidebar)
  const isFlowRunning = useFlowRunStore((state) => state.isFlowRunning)
  const currentRunId = useFlowRunStore((state) => state.currentRunId)

  // Eval state for conditional rendering
  const isEvalRunning = useEvalStore((state) => state.isEvalRunning)
  const isPaused = useEvalStore((state) => state.isPaused)
  const evalEvents = useEvalStore((state) => state.events)
  const hasEvalData = evalEvents.length > 0
  const { mutate: initializePlayground, isPending: isInitializingPlayground } =
    useInitializePlaygroundMutation()
  const { mutate: triggerApiRequest, isPending: isTriggeringApiRequest } =
    useTriggerApiRequestMutation()
  const resetFlowRunStore = useFlowRunStore((state) => state.reset)
  const setIsFlowRunning = useFlowRunStore((state) => state.setIsFlowRunning)
  const { publishFlow, isSavingDraft, isPublishingFlow, isPublishing } = usePublishFlow()

  const { getSettingsFormValues } = useAgentSettingsFormValues()

  // Use flowData.phlo.phlo_type as fallback if agentType from store is not available
  const effectiveAgentType = agentType || flowData?.phlo?.phlo_type
  // Off in every embed session: each one is a sub-account session, and the test routes refuse those.
  const isHiddenInEmbed = isEmbed
  const isVoiceFlow =
    effectiveAgentType === FlowTypes.incoming_call || effectiveAgentType === FlowTypes.outgoing_call
  const isVoiceNatChecking = isVoiceFlow && isNatChecking
  const isNetworkBlocked = isBehindNat && isVoiceFlow

  // Playground lands the test call in the agent's browser via the Plivo browser SDK,
  // which only registers when the org has SIP endpoint credentials (see PhoneHandler).
  // A flow needs them if it is a voice flow or contains a Call Customer (initiate_call) node.
  // Without them the call fails with an opaque backend error, so block here and tell the
  // user to re-login (login regenerates the credentials if missing).
  const sipEndpoint = profileData?.organizations?.[0]?.sip_endpoint
  const hasSipEndpoint = Boolean(sipEndpoint?.username && sipEndpoint?.password)
  // flowData is builder-format (inverseTransformFlowData collapses the
  // outbound-screening composite), so dial capability must be checked via the
  // call-placing set, not the raw initiate_call type.
  const flowPlacesCall =
    isVoiceFlow ||
    (flowData?.nodes?.some((node) => CALL_PLACING_NODE_TYPES.has(node.type ?? '')) ?? false)
  const isMissingSipEndpoint = Boolean(profileData) && flowPlacesCall && !hasSipEndpoint

  // Combined loading state for API request flows
  const isLoading = isInitializingPlayground || isTriggeringApiRequest || isSubmittingApiRequest

  // Test republishes the agent and a migrated legacy one is live on real numbers,
  // so it stays shut even though editing is now allowed. Duplicates unaffected.
  const isLegacyAgent = isMigratedLegacyFlow(flowData?.phlo)

  const getDisabledReason = () => {
    if (!flowData?.phlo.id) return 'Please save the flow first'
    if (isLegacyAgent) return 'Testing is not available for legacy agents'
    if (isInitializingPlayground) return 'Initializing playground...'
    if (isFlowRunning) return 'Agent is already running'
    if (isPublishingFlow) return 'Publishing...'
    if (isSavingDraft) return 'Saving...'
    return undefined
  }

  const disabledReason = getDisabledReason()

  const handlePlaygroundClick = async () => {
    if (!flowData) return
    setShowVibeSidebar(false)
    const formValues = getSettingsFormValues()
    checkUnsavedChanges(true, async () => {
      try {
        // Nothing changed since last save: skip the republish (which would mint a
        // new phlo version + a duplicate set of node rows) and run the saved flow.
        if (isFlowCleanForPlayground(flowData, formValues)) {
          initializePlayground(flowData)
          return
        }
        // First publish/save as draft, wait for ai_agent_v2 meta-compile, then
        // initialize the playground. Without the wait the runner picks up the
        // saved node before `_meta` lands and the call falls back to the
        // free-form CXAgent path. Soft timeout -- still init on timeout.
        await publishFlow(flowData, {
          isDraft: flowData.phlo.state === AgentState.DRAFT,
          name: flowData.phlo.name,
          isPlayground: true,
          agentSettingsOverride: formValues,
          onSuccess: async () => {
            if (!flowData.phlo.id) {
              initializePlayground(flowData)
              return
            }
            setIsWaitingForMeta(true)
            try {
              await waitForMetaReady(flowData.phlo.id)
            } finally {
              setIsWaitingForMeta(false)
            }
            initializePlayground(flowData)
          },
        })
      } catch (error) {
        // Handle validation errors from publishFlow
        agentLogger.error('Failed to publish flow:', error)
      }
    })
  }

  const handleButtonClick = () => {
    // Embed mounts no browser phone, so a test call has nothing to ring.
    if (isEmbed && flowPlacesCall) {
      embedTestErrorToast("Calls can't be tested in the embedded builder.")
      return
    }

    // Embed has no connection banner, and without realtime the run never reports back, so the
    // test would sit on "Starting..." with nothing saying why.
    const { pubnubStatus } = useTabStore.getState()
    if (
      isEmbed &&
      (pubnubStatus === PubnubStatus.IDLE || pubnubStatus === PubnubStatus.DISCONNECTED)
    ) {
      embedTestErrorToast('Live updates are not connected. Reload the page and try again.')
      return
    }

    // Can't place the test call without SIP endpoint credentials for the agent's browser
    if (isMissingSipEndpoint) {
      errorToast({
        title: 'Unable to place call',
        description: 'Please re-login and try again.',
      })
      return
    }

    // Check for NAT/firewall restriction on voice flows
    if (isNetworkBlocked) {
      setPopoverContent(PopoverType.NAT)
      setIsPopoverOpen(true)
      return
    }

    // Don't show balance warning if data is still loading
    if (!canTest && !effectiveBalanceLoading) {
      setPopoverContent(PopoverType.BALANCE)
      setIsPopoverOpen(true)
      return
    }

    if (effectiveAgentType === FlowTypes.api_request) {
      setPopoverContent(PopoverType.API)
      setIsPopoverOpen(true)
    } else {
      handlePlaygroundClick()
    }
  }

  const handleApiRequestSubmit = async (payload: Record<string, JsonValue>) => {
    if (!flowData?.phlo.id) return

    setIsSubmittingApiRequest(true)
    try {
      // Ensure we have a valid name
      const flowName = flowData.phlo.name || 'Agent Flow'
      const formValues = getSettingsFormValues()

      const runApiPlayground = async () => {
        try {
          if (!flowData.phlo.id) return
          // Wait for ai_agent_v2 meta-compile to finish before starting the
          // playground; see handlePlaygroundClick for rationale.
          setIsWaitingForMeta(true)
          try {
            await waitForMetaReady(flowData.phlo.id)
          } finally {
            setIsWaitingForMeta(false)
          }
          // Initialize the playground to get the flow details
          const resp = await flowService.initializePlayground(flowData.phlo.id, {
            message: 'User first message',
          })
          const { flow_uuid: flowId, auth_id: authId } = resp.data

          // Reset flow run store and set running state
          resetFlowRunStore()
          setIsFlowRunning(true)

          // Process payload to support nested objects, arrays, and primitives
          const processedPayload: Record<string, JsonValue> = {}
          Object.entries(payload).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              processedPayload[key] = value
            }
          })

          // Trigger the API request with the payload as body
          triggerApiRequest(
            {
              flowId,
              authId,
              queryParams: { ispg: true },
              body: processedPayload,
              isLegacy: flowData.phlo.is_legacy,
            },
            {
              onSettled: () => {
                setIsSubmittingApiRequest(false)
              },
            },
          )

          setIsPopoverOpen(false)
        } catch (error) {
          setIsSubmittingApiRequest(false)
          // The fallback re-hits the route that just failed and, on success, runs the agent
          // without this payload, so embed reports the failure instead.
          if (isEmbed) {
            embedTestErrorToast(getErrorText(error as AxiosError))
            setIsPopoverOpen(false)
            return
          }
          // Fallback to regular initialization if the custom approach fails
          initializePlayground(flowData)
          setIsPopoverOpen(false)
        }
      }

      // Nothing changed since last save: skip the republish and run directly.
      if (isFlowCleanForPlayground(flowData, formValues)) {
        await runApiPlayground()
        return
      }

      // First publish/save as draft, then proceed with API request
      await publishFlow(flowData, {
        isDraft: flowData.phlo.state === AgentState.DRAFT,
        name: flowName,
        isPlayground: true,
        agentSettingsOverride: formValues,
        onSuccess: runApiPlayground,
        // Validation guards early-return without throwing or onSuccess — the
        // spinner set above must be reset here or it wedges forever.
        onResult: (result) => {
          if (result.status === 'failed') {
            setIsSubmittingApiRequest(false)
            setIsPopoverOpen(false)
          }
        },
      })
    } catch (_error) {
      // This will now properly catch validation errors from publishFlow
      setIsSubmittingApiRequest(false)
      setIsPopoverOpen(false)
    }
  }

  const handleCancel = () => {
    setIsPopoverOpen(false)
  }

  const isStarting =
    isInitializingPlayground ||
    (isFlowRunning && !currentRunId) ||
    isPublishingFlow ||
    isSavingDraft ||
    isWaitingForMeta

  const handleManualTest = useCallback(() => {
    // The vibe agent's trigger bypasses the rendered button, so it gets the same gate.
    if (isHiddenInEmbed) return
    if (!!disabledReason || isPublishing || isStarting || effectiveBalanceLoading) return
    handleButtonClick()
  }, [
    isHiddenInEmbed,
    disabledReason,
    isPublishing,
    isStarting,
    canTest,
    effectiveBalanceLoading,
    effectiveAgentType,
    flowData,
    setShowVibeSidebar,
    publishFlow,
    initializePlayground,
    isNetworkBlocked,
  ])

  // Subscribe to TRIGGER_MANUAL_TEST_AGENT event from the vibe agent
  useEffect(() => {
    const unsubscribe = primaryEventBus.on(PrimaryEvents.TRIGGER_MANUAL_TEST_AGENT, () => {
      handleManualTest()
    })
    return unsubscribe
  }, [handleManualTest])

  // Embed adopts only a run announced for its own session, so without one the button would stay on
  // "Starting..." for good. Waits as long as the console's stream path waits for a run to confirm.
  const isAwaitingEmbedRun = isEmbed && isFlowRunning && !currentRunId
  useEffect(() => {
    if (!isAwaitingEmbedRun) return
    const timer = setTimeout(() => {
      setIsFlowRunning(false)
      embedTestErrorToast('The test did not report back in time. Try again.')
    }, RUN_CONFIRM_POLL_MS * RUN_CONFIRM_MAX_TRIES)
    return () => clearTimeout(timer)
  }, [isAwaitingEmbedRun, setIsFlowRunning])

  const isAllowedType =
    effectiveAgentType &&
    [
      FlowTypes.api_request,
      FlowTypes.incoming_call,
      FlowTypes.incoming_whatsapp,
      FlowTypes.incoming_sms,
      FlowTypes.incoming_chat,
    ].includes(effectiveAgentType as FlowTypes)

  const startingLabel = isWaitingForMeta ? 'Optimising agent...' : 'Starting...'

  if (isHiddenInEmbed) {
    return null
  }

  // If neither eval feature flag is enabled, show the simple button. Embed always does: the eval
  // and vibe entries have no embed routes.
  if (isEmbed || (!isManualEvalEnabled && !isVibeEvalEnabled)) {
    // Only show the button if it's an allowed type
    if (!isAllowedType) {
      return null
    }

    return (
      <div className="relative">
        <ButtonWithLoader
          id="playground-test-agent-button"
          variant="outline"
          size="sm"
          typography="action"
          onClick={handleButtonClick}
          disabled={
            !!disabledReason || isPublishing || effectiveBalanceLoading || isVoiceNatChecking
          }
          disabledReason={disabledReason}
          loading={isStarting || effectiveBalanceLoading || isVoiceNatChecking}
          data-testid="button-test-agent"
          data-role="playground-test-agent-button"
        >
          {!(isStarting || effectiveBalanceLoading || isVoiceNatChecking) && (
            <Play className="h-4 w-4 mr-2" />
          )}
          {isStarting
            ? startingLabel
            : effectiveBalanceLoading
              ? 'Loading...'
              : isVoiceNatChecking
                ? 'Checking...'
                : 'Test agent'}
        </ButtonWithLoader>

        {/* Custom popover without portal - renders directly in DOM */}
        {isPopoverOpen && (
          <>
            {/* Backdrop to close popover when clicking outside */}
            {/* biome-ignore lint/a11y/useSemanticElements: not using semantic elements */}
            <div
              className="fixed inset-0 z-40"
              onClick={handleCancel}
              onKeyDown={(e) => e.key === 'Escape' && handleCancel()}
              role="button"
              tabIndex={0}
              aria-label="Close popover"
              data-testid="playground-popover-backdrop-close"
            />

            {/* Popover content */}
            <div className="absolute top-full right-0 mt-2 z-50 bg-popover border border-st1 rounded-md shadow-lg animate-in fade-in-0 zoom-in-95 duration-200">
              {popoverContent === PopoverType.BALANCE && (
                <BalanceWarningPopoverContent
                  balance={balance}
                  currency={currency}
                  hasError={hasError}
                  onClose={handleCancel}
                />
              )}

              {popoverContent === PopoverType.NAT && (
                <NatWarningPopoverContent
                  onClose={handleCancel}
                  onRetry={() => {
                    handleCancel()
                    recheckNat()
                  }}
                />
              )}

              {popoverContent === PopoverType.API &&
                effectiveAgentType === FlowTypes.api_request && (
                  <div className="p-0">
                    <ApiRequestPayloadForm
                      onSubmit={handleApiRequestSubmit}
                      onCancel={handleCancel}
                      isLoading={isLoading}
                    />
                  </div>
                )}
            </div>
          </>
        )}
      </div>
    )
  }

  // If any eval feature flag is enabled, show the dropdown with eval options
  const triggerButton = (
    <Button variant="outline" size="sm" typography="action">
      Test Agent
    </Button>
  )

  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Test with Vibe Agent invokes the vibe/AI agent — withhold it when the
              vibe agent is disabled, even if the vibe-eval flag is on (SER-6067). */}
          {isVibeEvalEnabled && !vibeAgentDisabled && (
            <DropdownMenuItem data-testid="menu-item-test-with-vibe">
              <TestWithVibeButton />
            </DropdownMenuItem>
          )}
          {isAllowedType && (
            <DropdownMenuItem data-testid="menu-item-manual-test">
              <ButtonWithLoader
                id="playground-test-agent-button"
                data-testid="button-test-agent"
                data-role="playground-test-agent-button"
                variant="ghost"
                size="xs"
                typography="action"
                type="button"
                onClick={handleManualTest}
                disabled={
                  !!disabledReason || isPublishing || effectiveBalanceLoading || isVoiceNatChecking
                }
                disabledReason={disabledReason}
                loading={isStarting || effectiveBalanceLoading || isVoiceNatChecking}
              >
                {!(isStarting || effectiveBalanceLoading || isVoiceNatChecking) && (
                  <Play className="h-4 w-4 mr-2" />
                )}
                {isStarting
                  ? startingLabel
                  : effectiveBalanceLoading
                    ? 'Loading...'
                    : isVoiceNatChecking
                      ? 'Checking...'
                      : 'Manual test'}
              </ButtonWithLoader>
            </DropdownMenuItem>
          )}

          {isManualEvalEnabled && !isEvalRunning && (
            <DropdownMenuItem data-testid="menu-item-automatic-test">
              <StartEvalButton />
            </DropdownMenuItem>
          )}
          {isManualEvalEnabled && (isEvalRunning || isPaused || hasEvalData) && (
            <DropdownMenuItem data-testid="menu-item-view-results">
              <ViewResultsButton />
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Custom popover without portal - renders directly in DOM */}
      {isPopoverOpen && (
        <>
          {/* Backdrop to close popover when clicking outside */}
          {/* biome-ignore lint/a11y/useSemanticElements: not using semantic elements */}
          <div
            className="fixed inset-0 z-40"
            onClick={handleCancel}
            onKeyDown={(e) => e.key === 'Escape' && handleCancel()}
            role="button"
            tabIndex={0}
            aria-label="Close popover"
            data-testid="playground-popover-backdrop-close"
          />

          {/* Popover content */}
          <div className="absolute top-full right-0 mt-2 z-50 bg-popover border border-st1 rounded-md shadow-lg animate-in fade-in-0 zoom-in-95 duration-200">
            {popoverContent === PopoverType.BALANCE && (
              <BalanceWarningPopoverContent
                balance={balance}
                currency={currency}
                hasError={hasError}
                onClose={handleCancel}
              />
            )}

            {popoverContent === PopoverType.NAT && (
              <NatWarningPopoverContent
                onClose={handleCancel}
                onRetry={() => {
                  handleCancel()
                  recheckNat()
                }}
              />
            )}

            {popoverContent === PopoverType.API && effectiveAgentType === FlowTypes.api_request && (
              <div className="p-0">
                <ApiRequestPayloadForm
                  onSubmit={handleApiRequestSubmit}
                  onCancel={handleCancel}
                  isLoading={isLoading}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
